import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/solid';
import { Visualizer } from 'react-sound-visualizer';
import { useAuth } from '../../../auth/AuthContext';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../config/colors';

const WebSpeechRecorder = forwardRef(({ onTranscriptionUpdate, onError, onReset, onStop, helperText, priority = 'normal', transcriptSegments = [] }, ref) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const recognition = useRef(null);
  const mediaStream = useRef(null);
  const lastProcessedIndex = useRef(-1);
  const texts = useRef([...transcriptSegments]);

  // Clean up any existing recognition instance
  const cleanupRecognition = useCallback(() => {
    if (recognition.current) {
      try {
        recognition.current.stop();
        recognition.current.onresult = null;
        recognition.current.onerror = null;
        recognition.current.onend = null;
      } catch (e) {
        console.warn('Error cleaning up recognition:', e);
      }
      recognition.current = null;
    }
  }, []);

  const initializeSpeechRecognition = useCallback(() => {
    try {
      // Check browser support
      if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
        throw new Error('browser_not_supported');
      }

      // Clean up any existing instance first
      cleanupRecognition();

      console.log('Start SpeechRecognition');

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      // Configure recognition
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = user?.preferences?.language === 'fr' ? 'fr-FR' : 
                                user?.preferences?.language === 'de' ? 'de-DE' :
                                user?.preferences?.language === 'it' ? 'it-IT' : 'en-US';


        if (texts.current.length > 0 && texts.current[texts.current.length - 1] !== '') {
          texts.current.push('');
        }

      // Handle results
      recognitionInstance.onresult = (event) => {
        // Use current transcriptSegments as the base for the texts array
        const currentTranscriptSegments = texts.current.length === 0 ? [...transcriptSegments] : texts.current;
        

        let partial = '';
        let isFinal = false;
        const updatedTexts = [...currentTranscriptSegments];

        
        for (let i = event.resultIndex; i < event.results.length; ++i) {
            if (event.results[i].isFinal) {
                // Remove the last partial result if it exists
                if (updatedTexts.length > 0) {
                  updatedTexts.pop();
                  if (updatedTexts[updatedTexts.length - 1] === '') {
                    updatedTexts.pop();
                  }
                }
                updatedTexts.push(event.results[i][0].transcript.trim());
                updatedTexts.push(''); // Add empty string for next partial result
                texts.current = updatedTexts;
                onTranscriptionUpdate?.([...updatedTexts]);
                isFinal = true;
            } else {
                partial += event.results[i][0].transcript;
            }
        }
        if (!isFinal) {
            // console.log('coming in there not final', updatedTexts);
            // Remove any existing partial result
            if (updatedTexts.length > 0 && updatedTexts[updatedTexts.length - 1] !== '') {
              updatedTexts.pop();
              // console.log('coming in there removing stuff', updatedTexts);
            }
            updatedTexts.push(partial);
            texts.current = updatedTexts;
            // console.log('coucou not final', updatedTexts);
            onTranscriptionUpdate?.([...updatedTexts]);
        }
      };

      // Handle errors
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        let errorMessage;
        
        switch (event.error) {
          case 'not-allowed':
            errorMessage = t('audioRecorder.errors.permissions');
            break;
          case 'network':
            errorMessage = t('audioRecorder.errors.connection');
            break;
          case 'no-speech':
            // Don't show error for no speech detected
            return;
          case 'aborted':
            // Don't show error if we manually aborted
            if (!isRecording) return;
            errorMessage = t('audioRecorder.errors.aborted');
            break;
          default:
            errorMessage = t('audioRecorder.errors.generic');
        }
        
        onError?.(errorMessage);
        // Stop recording completely
        stopRecording();
      };

      // Handle end event and restart if still recording
      recognitionInstance.onend = () => {
        if (isRecording && recognition.current === recognitionInstance) {
          try {
            recognitionInstance.start();
          } catch (e) {
            console.warn('Could not restart recognition:', e);
            setIsRecording(false);
          }
        } else {
          // Clean up if we're not recording anymore
          if (mediaStream.current) {
            mediaStream.current.getTracks().forEach(track => track.stop());
            mediaStream.current = null;
            setAudioStream(null);
          }
          setIsRecording(false);
        }
      };

      recognition.current = recognitionInstance;
      return recognitionInstance;
    } catch (error) {
      if (error.message === 'browser_not_supported') {
        onError?.(t('audioRecorder.errors.browserNotSupported'));
      } else {
        onError?.(t('audioRecorder.errors.initialization'));
      }
      console.error('Speech recognition initialization error:', error);
      return null;
    }
  }, [onError, onTranscriptionUpdate, t, user?.preferences?.language, cleanupRecognition, isRecording]);

  const stopRecording = useCallback(() => {
    console.log('Stopping the record');
    
    setIsRecording(false);
    
    // Stop speech recognition first
    cleanupRecognition();
    
    // Then stop the media stream
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach(track => track.stop());
      mediaStream.current = null;
    }
    
    setAudioStream(null);
    lastProcessedIndex.current = -1; // Reset the index when stopping
    onStop?.();
  }, [onStop, cleanupRecognition]);

  const startRecording = useCallback(async () => {
    // Reset texts to current transcriptSegments at the start of each session
    texts.current = [...transcriptSegments];
    try {
      // Initialize speech recognition each time we start recording
      const recognitionInstance = initializeSpeechRecognition();
      if (!recognitionInstance) {
        throw new Error('Failed to initialize speech recognition');
      }
      
      // Start speech recognition
      lastProcessedIndex.current = -1; // Reset the index when starting new recording
      recognitionInstance.start();
      setIsRecording(true);
      
      try {
        // Request microphone access after speech recognition is started
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            channelCount: 1,
            sampleRate: 16000,
            sampleSize: 16,
            echoCancellation: true,
            noiseSuppression: true
          }
        });
        
        mediaStream.current = stream;
        setAudioStream(stream);
      } catch (streamError) {
        console.error('Error getting media stream:', streamError);
        // If we fail to get the stream, stop speech recognition
        stopRecording();
        onError?.(t('audioRecorder.errors.permissions'));
      }
    } catch (error) {
      console.error('Error starting recording:', error);
      // Ensure everything is cleaned up in case of error
      if (mediaStream.current) {
        mediaStream.current.getTracks().forEach(track => track.stop());
        mediaStream.current = null;
      }
      cleanupRecognition();
      setAudioStream(null);
      setIsRecording(false);
      onError?.(t('audioRecorder.errors.permissions'));
    }
  }, [initializeSpeechRecognition, onError, t, stopRecording, transcriptSegments]);

  const handleToggleRecording = useCallback((e) => {
    // Prevent form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // Remove the automatic initialization useEffect - we'll initialize only when starting recording

  useEffect(() => {
    const handleKeyPress = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'x') {
        // Check if we're inside a modal (higher priority context)
        const isInModal = document.querySelector('[role="dialog"]') !== null;
        const isInFeedbackModal = document.querySelector('[data-feedback-modal]') !== null;
        
        // If this recorder has 'high' priority (feedback modal) or if no modal is open
        const shouldHandle = priority === 'high' || (!isInModal && priority === 'normal');
        
        // Additional check: if we're in a feedback modal, only high priority recorders should respond
        if (isInFeedbackModal && priority !== 'high') {
          console.log(`WebSpeech Recorder (priority: ${priority}) - Skipping, feedback modal is open`);
          return;
        }
        
        if (shouldHandle) {
          console.log(`WebSpeech Recorder (priority: ${priority}) - Handling shortcut`);
          event.preventDefault();
          event.stopPropagation();
          handleToggleRecording();
        } else {
          console.log(`WebSpeech Recorder (priority: ${priority}) - Not handling, modal is open`);
        }
      }
    };

    // Use capture phase for high priority recorders to intercept first
    const useCapture = priority === 'high';
    window.addEventListener('keydown', handleKeyPress, useCapture);
    return () => {
      window.removeEventListener('keydown', handleKeyPress, useCapture);
    };
  }, [handleToggleRecording, priority]);

  useEffect(() => {
    if (onReset) {
      return () => {
        if (isRecording) {
          stopRecording();
        }
      };
    }
  }, [onReset, isRecording, stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupRecognition();
      if (mediaStream.current) {
        mediaStream.current.getTracks().forEach(track => track.stop());
        mediaStream.current = null;
      }
    };
  }, [cleanupRecognition]);

  useImperativeHandle(ref, () => ({
    stopRecording
  }));

  return (
    <div className="px-6 py-4">
      {/* Main Horizontal Controls */}
      <div className="flex items-center justify-between space-x-6 mb-4">
        {/* Left side - Audio Visualizer */}
        <div className="flex-1">
          {isRecording && audioStream ? (
            <Visualizer
              audio={audioStream}
              autoStart={true}
              strokeColor="#c5d6f8"
              mode="continuous" 
              rectWidth={3} 
              slices={50}
              heightNorm={1}
            >
              {({ canvasRef }) => (
                <canvas 
                  ref={canvasRef} 
                  style={{ height: '80px', width: '100%' }}
                  className="rounded-lg"
                />
              )}
            </Visualizer>
          ) : (
            <div className="flex items-center justify-center h-[80px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-500">
                {isRecording ? t('audioRecorder.recording') : helperText || t('audioRecorder.clickToStart')}
                <span className="ml-2 text-xs text-gray-400">
                  {t('audioRecorder.shortcut')}
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Right side - Control Button */}
        <div className="flex items-center">
          {/* Record/Stop Button */}
          <button
            type="button"
            onClick={handleToggleRecording}
            className={`flex items-center justify-center w-16 h-16 rounded-full transition-all duration-200 focus:outline-none focus:ring-4 ${
              isRecording 
                ? 'bg-red-300 hover:bg-red-400 focus:ring-red-200' 
                : 'hover:bg-teal-400 focus:ring-teal-200'
            }`}
            style={!isRecording ? {
              backgroundColor: colors.primary.selected
            } : undefined}
            aria-label={isRecording ? t('audioRecorder.stopRecording') : t('audioRecorder.startRecording')}
          >
            {isRecording ? (
              <StopIcon className="w-8 h-8 text-white" />
            ) : (
              <MicrophoneIcon className="w-8 h-8 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Recording Status Bar */}
      {isRecording && (
        <div className="flex items-center justify-center space-x-2 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
            <span className="font-medium text-red-600">{t('audioRecorder.recordingStatus')}</span>
          </div>
        </div>
      )}
    </div>
  );
});

export default WebSpeechRecorder; 